/* eslint-disable sort-exports/sort-exports */
import { atom } from "jotai";
import { z } from "zod";

import { JotaiTool } from "../../index";

export const UpdateZapParams = z.object({
  zap_id: z.string().describe("REQUIRED: The ID of the Zap to update."),
  account_id: z
    .string()
    .describe(
      "REQUIRED: The account ID owning the Zap. This MUST be sourced from 'additionalData.canvas.account_id'. Do NOT ask the user for this value.",
    ),
  app: z
    .string()
    .describe(
      "REQUIRED: The ZDL app type for the Zap (e.g., 'EngineAPI'). Typically 'EngineAPI'.",
    ),
  action: z
    .string()
    .describe(
      "REQUIRED: The ZDL action for the Zap (e.g., 'series_skip_errors'). Typically 'series_skip_errors'.",
    ),
  steps: z
    .array(
      z.object({
        id: z.string().describe("REQUIRED: The ID of the step."),
        app: z
          .string()
          .describe(
            "REQUIRED: The full app ID for this step, which may include a version (e.g., 'TableCLIAPI@1.16.5' or 'slack'). Use the get_zap tool first to retrieve the correct app_id format for each existing step. This is CRITICAL for the update to succeed.",
          ),
        app_id: z
          .string()
          .describe("A short app name, like 'gmail' or 'slack'"),
        type: z
          .string()
          .describe(
            "REQUIRED: The type of step (e.g., 'read', 'write', 'filter').",
          ),
        action: z
          .string()
          .describe(
            "REQUIRED: The specific action key for this step (e.g., 'event_start', 'direct_message').",
          ),
        params: z
          .record(z.string(), z.any())
          .describe("REQUIRED: The parameters object for this step."),
        meta: z
          .record(z.string(), z.any())
          .optional()
          .describe("Optional: Metadata object for this step."),
        authentication_id: z
          .number()
          .int()
          .optional()
          .describe(
            "Optional: Authentication ID for this step, if applicable.",
          ),
        title: z.string().optional().describe("Optional: Title for this step."),
      }),
    )
    .min(1)
    .describe(
      "REQUIRED: The array of step objects defining the Zap's workflow. Must contain at least one step.",
    ),
  timezone: z
    .string()
    .optional()
    .describe(
      "Optional: The timezone for the Zap (e.g., 'America/Los_Angeles').",
    ),
  title: z
    .string()
    .optional()
    .describe("Optional: The overall title of the Zap."),
  description: z
    .string()
    .optional()
    .describe("Optional: The description of the Zap."),
  zap_note: z
    .string()
    .nullable()
    .optional()
    .describe("Optional: Notes about the Zap."),
  active: z
    .boolean()
    .optional()
    .describe(
      "Optional: Whether the Zap should be active (true) or inactive (false).",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Updating Zap settings...' or 'Saving field mappings...'",
    ),
});

export type UpdateZapParamsType = z.infer<typeof UpdateZapParams>;

export const updateZapExecute = async (rawArgs: any) => {
  let params: UpdateZapParamsType;
  try {
    params = UpdateZapParams.parse(rawArgs || {});
  } catch (error) {
    console.error("[UPDATE_ZAP_TOOL] Error parsing arguments:", error);
    return {
      $error: {
        message: `Error: Invalid arguments provided for update_zap. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
      },
    };
  }

  const missingAppIdSteps = params.steps.filter((step) => !step.app_id);
  if (missingAppIdSteps.length > 0) {
    const errorText = `Error: All steps must include an 'app_id'. Missing for step IDs: ${missingAppIdSteps.map((s) => s.id).join(", ")}. Use get_zap to fetch current app_ids.`;
    console.error("[UPDATE_ZAP_TOOL] Validation Error:", errorText);
    return {
      $error: {
        message: errorText,
      },
    };
  }

  // Process steps to ensure consistency
  const processedSteps = params.steps.map((step) => {
    const processedStep = { ...step };
    if (!processedStep.app && processedStep.app_id) {
      const appMatch = processedStep.app_id.match(/^([^@]+)(@|$)/);
      if (appMatch && appMatch[1]) {
        processedStep.app = appMatch[1];
      }
    }
    return processedStep;
  });

  // Build the ZDL structure for validation
  const gulliverZdl = {
    app: params.app,
    type: "run",
    action: params.action,
    id: `series_${params.zap_id}`,
    zdl_version: "0.4",
    title: params.title || "Untitled Zap",
    steps: processedSteps, // Use original steps for initial validation
    ...(params.timezone && { timezone: params.timezone }),
  };

  // Build the complete ZSL template for validation
  const zslForValidation = {
    name: params.title || "Unnamed ZSL for Validation",
    description: params.description || "",
    parameters: [],
    interfaces: [],
    tables: [],
    chatbots: [],
    zaps: [
      {
        id: params.zap_id, // Template level id
        zdl: gulliverZdl, // Use the exact same ZDL that will be sent to Gulliver
      },
    ],
    canvases: [],
    edges: [],
    genericAssets: [],
  };

  // ENHANCED: Pre-flight validation - validate parameters against needs before ZSL validation
  // TODO: Implement pre-flight validation when validation utilities are available
  // try {
  //   const { preFlightValidateSteps, formatPreFlightErrors } = await import(
  //     "../../shared/validation/preFlightValidation"
  //   );
  //   const preFlightResult = await preFlightValidateSteps(processedSteps);
  //   if (!preFlightResult.isValid) {
  //     const errorMessage = formatPreFlightErrors(preFlightResult);
  //     return {
  //       $error: {
  //         message: `${errorMessage}\n\nCannot proceed with update_zap. Please fix the parameter validation errors and try again.`,
  //       },
  //     };
  //   }
  // } catch {
  //   // Continue with ZDL validation even if pre-flight fails to avoid blocking updates
  // }

  // ENHANCED: ZDL structure validation - validate ZDL structure before ZSL validation
  // TODO: Implement ZDL validation when validation utilities are available
  // try {
  //   const { validateZdl, formatZdlValidationErrors } = await import(
  //     "../../shared/validation/zdlValidator"
  //   );
  //   const zdlResult = validateZdl(gulliverZdl);
  //   if (!zdlResult.isValid) {
  //     const errorMessage = formatZdlValidationErrors(zdlResult);
  //     return {
  //       $error: {
  //         message: `${errorMessage}\n\nCannot proceed with update_zap. Please fix the ZDL structure errors and try again.`,
  //       },
  //     };
  //   }
  // } catch {
  //   // Continue with auto-auth even if ZDL validation fails to avoid blocking updates
  // }

  // ENHANCED: Auto-auth selection - automatically configure authentication like Editor
  // TODO: Implement auto-auth when auth utilities are available
  const finalSteps = processedSteps;
  // try {
  //   const { enhanceStepsWithAutoAuth } = await import(
  //     "../../shared/auth/autoSelectAuth"
  //   );
  //   finalSteps = (await enhanceStepsWithAutoAuth(
  //     processedSteps,
  //   )) as typeof processedSteps;
  // } catch {
  //   finalSteps = processedSteps; // Fall back to original steps
  // }

  try {
    // Call validate ZSL endpoint via backend
    const validateRes = await fetch("/api/canvasui/composer/validateZsl", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(zslForValidation),
    });

    if (!validateRes.ok) {
      let validationErrorData: any = {
        error: `ZSL validation failed (${validateRes.status}) - Unknown error structure`,
      };
      try {
        validationErrorData = await validateRes.json();
      } catch {
        validationErrorData = {
          error: `ZSL validation failed (${validateRes.status}) - Response not valid JSON`,
        };
      }

      console.error(
        "[UPDATE_ZAP_TOOL] ZSL validation failed:",
        validationErrorData,
      );

      let formattedErrors = "ZSL Validation Failed:\n";
      if (
        validationErrorData.errors &&
        Array.isArray(validationErrorData.errors)
      ) {
        formattedErrors += validationErrorData.errors
          .map((err: any) => {
            let errorMsg = `- ${err.title || err.code}: ${err.detail}`;
            if (err.source?.pointer) {
              errorMsg += ` (at ${err.source.pointer})`;
            }
            return errorMsg;
          })
          .join("\n");
      } else {
        formattedErrors += JSON.stringify(validationErrorData, null, 2);
      }

      return {
        $error: {
          message: `${formattedErrors}\n\nCannot proceed with update_zap. Please fix the validation errors and try again.`,
        },
      };
    }

    const validationResult = await validateRes.json();

    // Check if validation passed
    let validationPassed = false;
    if (validationResult?.data?.valid === true) {
      validationPassed = true;
    } else if (validationResult?.valid === true) {
      validationPassed = true;
    } else if (validationResult?.errors) {
      // Validation failed with specific errors
      const errorText = `ZSL Validation Failed:\n${validationResult.errors
        .map((err: any) => `- ${err.title || err.code}: ${err.detail}`)
        .join("\n")}`;

      return {
        $error: {
          message: `${errorText}\n\nCannot proceed with update_zap. Please fix the validation errors and try again.`,
        },
      };
    }

    if (!validationPassed) {
      return {
        $error: {
          message: `ZSL Validation Failed: Unknown validation response format.\n\nCannot proceed with update_zap.`,
        },
      };
    }
  } catch (validationError) {
    console.error(
      "[UPDATE_ZAP_TOOL] Error during ZSL validation:",
      validationError,
    );
    return {
      $error: {
        message: `Error during ZSL validation: ${validationError instanceof Error ? validationError.message : String(validationError)}\n\nCannot proceed with update_zap.`,
      },
    };
  }

  // Proceed with update using validated and auth-enhanced data
  const bodyForProxy = {
    app: params.app,
    action: params.action,
    title: params.title,
    steps: finalSteps, // Use finalSteps which include auto-selected authentication
    ...(params.timezone && { timezone: params.timezone }),
    ...(params.description && { description: params.description }),
    ...(params.zap_note !== undefined && { zap_note: params.zap_note }),
    ...(params.active !== undefined && { active: params.active }),
  };

  const queryForProxy = new URLSearchParams({
    zap_id: params.zap_id,
    account_id: params.account_id,
  });
  const proxyApiUrl = `/api/canvasui/composer/updateZap?${queryForProxy.toString()}`;

  try {
    const res = await fetch(proxyApiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(bodyForProxy),
    });

    if (!res.ok) {
      let errorData: any = {
        error: `Proxy error for update_zap (${res.status}) - Unknown error structure`,
      };
      try {
        errorData = await res.json();
        if (!(errorData && errorData.error)) {
          errorData = {
            error: `Proxy error for update_zap (${res.status}) - Received non-standard error`,
            details: errorData,
          };
        }
      } catch {
        errorData = {
          error: `Proxy error for update_zap (${res.status}) - Response not valid JSON`,
        };
      }
      const errorDetailString =
        errorData.error +
        (errorData.details ? `: ${JSON.stringify(errorData.details)}` : "");
      console.error(
        `[UPDATE_ZAP_TOOL] Proxy error for URL ${proxyApiUrl}: ${errorDetailString}`,
      );
      throw new Error(errorDetailString);
    }

    try {
      const data = await res.json();
      return {
        content: [
          {
            type: "text",
            text: `✅ ZSL Validation: PASSED\n✅ Zap Update: SUCCESSFUL\n\nUpdate Result:\n${JSON.stringify(data, null, 2)}`,
          },
        ],
      };
    } catch (e) {
      console.error(
        `[UPDATE_ZAP_TOOL] Error parsing successful proxy response as JSON. URL: ${proxyApiUrl}, Status: ${res.status}. Error: ${e instanceof Error ? e.message : String(e)}`,
      );
      throw new Error(
        `Proxy returned success status (${res.status}) but its response body was not valid JSON.`,
      );
    }
  } catch (error) {
    console.error("[UPDATE_ZAP_TOOL] Error in updateZapExecute:", error);
    return {
      $error: {
        message: `✅ ZSL Validation: PASSED\n❌ Zap Update: FAILED\n\nError updating Zap: ${error instanceof Error ? error.message : String(error)}`,
      },
    };
  }
};

export const updateZapDescription = `This tool updates the user-configurable parameters *within* the steps of an existing Zap. This typically involves changing values like email recipients, message content, filter criteria, data to write to a spreadsheet, calendar event details, etc.

**🚨 CRITICAL: APP IDENTIFIER VALIDATION 🚨**
Before calling update_zap, ENSURE you have:
1. Used get_app_name to fetch the correct app identifier
2. NOT guessed or assumed any app naming patterns
3. Used the EXACT implementation_id from get_app_name

Common "deleted app" errors and fixes:
- FilterCLIAPI → Use get_app_name("filter") to get correct ID
- FormatterCLIAPI → Use get_app_name("formatter") to get correct ID
- Any guessed pattern → ALWAYS use get_app_name first

**🚀 ENHANCED WITH EDITOR-INSPIRED FEATURES:**
- **Multi-layer Validation**: Validates parameters against needs, ZDL structure, and ZSL format
- **Auto-Authentication**: Automatically selects authentication from existing steps or suggests best option
- **Parallel Processing**: Validates multiple steps in parallel for improved performance
- **Type Safety**: Validates field types (string, number, boolean) against expected needs
- **Error Prevention**: Catches common field mapping errors before they reach Zapier

**🔒 AUTOMATIC VALIDATION LAYERS: This tool now automatically runs multiple validation layers before updating:**
1. **Parameter Validation**: Checks field names, types, and required values against app needs
2. **ZDL Structure Validation**: Validates step structure, references, and configuration
3. **Auto-Auth Selection**: Automatically configures authentication where possible
4. **ZSL Validation**: Final validation of complete Zapier Specification Language
You no longer need to call validate_zsl separately - comprehensive validation is built-in.

**CRITICAL WORKFLOW: Follow this sequence for successful updates:**
1.  **Construct the *complete proposed ZDL*** for the updated Zap. Start with the **entire ZDL structure** obtained from \`get_zap\`. Then, *only* modify the specific fields within the \`params\` object of the relevant step(s), as detailed under "WHAT YOU ARE UPDATING" below.
2.  **Call \`update_zap\` with the complete ZDL** - it will automatically validate the ZSL first and only proceed with the update if validation passes.
3.  **TO CONSTRUCT THE ZDL YOU CAN CALL \`get_zap\` first:** Fetch the complete current configuration (ZDL - Zap Object Definition Language) of the Zap. This output is your **source of truth** for all structural and immutable parameters.
4.  **(If needed for specific fields) Use \`needs_api\` and \`choices_api\`:** Based on data from \`get_zap\`, use these tools to determine valid options for the specific step parameters (\`params\` object within a step) you intend to change.
5.  **BEFORE calling update_zap make sure you are over 75% confident in your guesses for each field you're updating. If you aren't confident pause to ask the user for clarification first.**
6.  **If you encounter authentication issues or cannot retrieve valid field choices from the API, use the get_app_auth tool to verify authentication status and guide the user through the authentication process if needed.**

**CORE TASK: Your ONLY goal with this tool is to update the values *inside* the \`params\` object of one or more steps in the Zap's \`steps\` array. These \`params\` control the specific behavior and data handling of each step.**
Always preserve the exact app field value from get_zap output
Never split or modify the app identifier format
Only add app_id if it was already present in the original structure
Be more explicit about which fields are truly immutable vs. which can be modified
**--- PARAMETER CATEGORIES ---**

**A. DO NOT CHANGE THESE - CRITICAL IMMUTABLE PARAMETERS:**
   These parameters define the Zap's fundamental structure, identity, and app integrations. **YOU MUST NOT CHANGE, MODIFY, OR HALLUCINATE THESE VALUES.** They **MUST** be taken directly from the output of the \`get_zap\` tool and passed through verbatim to \`update_zap\`. Altering them will break the Zap.
    - \`zap_id\`: (String) The master ID of the Zap to update.
    - \`account_id\`: (String) The ID of the account owning the Zap. (Source from \`additionalData.canvas.account_id\`).
    - \`app\`: (String) The top-level ZDL application type for the Zap (typically 'EngineAPI').
    - \`action\`: (String) The top-level ZDL action for the Zap (typically 'series_skip_errors').
    - \`steps\`: (Array of step objects) The array itself and the following properties *of each step object itself* are IMMUTABLE:
        - \`id\`: (String) The unique ID of the step.
        - \`app\`: (String) The short app slug for this step (e.g., 'gmail', 'slack').
        - \`app_id\`: (String) **CRITICALLY IMMUTABLE.** The full app identifier for this step, often including a version (e.g., 'GmailCLIAPI@1.5.0', 'GoogleSheetsCLIAPI@1.12.0'). **NEVER CHANGE ANY PART OF THIS \`app_id\` STRING, INCLUDING THE VERSION.**
        - \`type\`: (String) The type of step (e.g., 'read', 'write', 'filter').
        - \`action\`: (String) The specific action key for this step (e.g., 'event_start', 'direct_message', 'create_spreadsheet_row').
        - \`authentication_id\`: (Number, Optional) The ID for the authenticated account used by the step. **DO NOT CHANGE THIS unless the specific task is to re-authenticate the step with a new connection for the *exact same \`app_id\`*. For general field mapping updates, this is IMMUTABLE.**
    - **Overall Zap Settings (Generally DO NOT CHANGE with this tool unless explicitly asked to change Zap metadata):**
        - \`title\` (overall Zap title), \`description\` (overall Zap description), \`timezone\`, \`zap_note\`, \`active\` (Zap on/off status).
    - **Individual Step Settings (Generally DO NOT CHANGE with this tool unless explicitly asked to change step metadata):**
        - \`title\` (custom title for a step), \`meta\` (metadata object for a step).

**B. WHAT YOU ARE UPDATING (Your ONLY Focus for field mapping):**
    - **Step-Specific Parameters:**
        - These are located *inside* the \`params\` object for each step within the \`steps\` array (e.g., \`steps[0].params.recipient_email\`, \`steps[1].params.message_text\`).
        - **This \`params\` object is the *only* part of a step you should modify with this tool for typical field updates.**
        - These fields control what each step *does*: e.g., the recipient for an email, the content of a Slack message, the criteria for a filter, the specific data values to map into a Google Sheet row, the summary or description for a Google Calendar event.
        - Use the \`needs_api\` and \`choices_api\` tools (based on data from \`get_zap\`) to discover the available fields within a step's \`params\` object and their valid values.

**🔒 BUILT-IN VALIDATION: This tool automatically validates the complete ZDL before updating. If validation fails, you'll receive detailed error messages. If validation passes, the Zap update will proceed automatically.**
`;

export const updateZapAtom = atom<JotaiTool<z.infer<typeof UpdateZapParams>>>(
  () => ({
    name: "update_zap",
    description: updateZapDescription,
    parameters: UpdateZapParams,
    execute: atom(null, async (_get, _set, context) => {
      const { args } = context;
      return updateZapExecute(args);
    }),
  }),
);
updateZapAtom.debugLabel = "updateZapAtom";
/* eslint-enable sort-exports/sort-exports */
